import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

const OtpVerificationDialog = ({
    open,
    onClose,
    onVerify,
    onResend,
    isVerifying = false,
    isResending = false,
    resendInitialSeconds = 60,
    errorMessage,
    email,
    isMaxView = false,
}) => {
    const { t } = useTranslation("dashboard");
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const [resendTimer, setResendTimer] = useState(resendInitialSeconds);
    const [canResend, setCanResend] = useState(false);
    const inputRefs = useRef([]);

    useEffect(() => {
        if (open) {
            setOtp(["", "", "", "", "", ""]);
            setResendTimer(resendInitialSeconds);
            setCanResend(false);
            setTimeout(() => inputRefs.current[0]?.focus(), 300);
        }
    }, [open, resendInitialSeconds]);

    useEffect(() => {
        if (!open) return;
        if (resendTimer > 0) {
            const timer = setTimeout(() => {
                setResendTimer((prev) => prev - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else {
            setCanResend(true);
        }
    }, [resendTimer, open]);

    const handleOtpChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;
        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text").replace(/\D/g, "");
        if (pastedData.length === 6) {
            const newOtp = pastedData.split("").slice(0, 6);
            setOtp(newOtp);
            inputRefs.current[5]?.focus();
        }
    };

    const handleVerifyOTP = () => {
        const otpString = otp.join("");
        if (otpString.length === 6) {
            onVerify(otpString);
        }
    };

    const handleResendOTP = () => {
        if (!canResend || isResending) return;
        onResend();
        setResendTimer(resendInitialSeconds);
        setCanResend(false);
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            sx={{
                zIndex: 1500,
                '& .MuiDialog-container': {
                    transform: isMaxView ? 'translateX(183px)' : 'none',
                    transition: 'transform 0.3s ease-in-out',
                },
            }}
        >
            <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
                {t("otpVerification")}
                <IconButton
                    aria-label={t("close")}
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        right: 10,
                        top: 10,
                        color: (theme) => theme.palette.grey[500],
                    }}
                    size="large"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Typography
                    variant="subtitle2"
                    align="center"
                    sx={{ mb: 3, color: "text.secondary" }}
                >
                    {email
                        ? t("otpSentToEmailPrompt", { email })
                        : t("otpSentToYourEmail")}
                </Typography>
                <FormControl fullWidth sx={{ mb: 3 }}>
                    <Box
                        sx={{
                            display: "flex",
                            gap: { sm: 3, xs: 2 },
                            justifyContent: "center",
                        }}
                    >
                        {otp.map((digit, index) => (
                            <TextField
                                key={index}
                                inputRef={(el) => {
                                    if (el) inputRefs.current[index] = el;
                                }}
                                value={digit}
                                onChange={(e) => handleOtpChange(index, e.target.value)}
                                onKeyDown={(e) => handleKeyDown(index, e)}
                                onPaste={handlePaste}
                                inputProps={{
                                    maxLength: 1,
                                    style: {
                                        textAlign: "center",
                                        fontSize: "24px",
                                    },
                                }}
                                sx={{
                                    width: "48px",
                                    height: "100%",
                                    "& .MuiOutlinedInput-root": {
                                        height: "48px",
                                        "& fieldset": {
                                            borderColor: "gray",
                                            borderWidth: "1px",
                                        },
                                        "&:hover fieldset": {
                                            borderColor: "primary.main",
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: "primary.main",
                                        },
                                    },
                                }}
                            />
                        ))}
                    </Box>
                </FormControl>

                {errorMessage && (
                    <Typography color="error" align="center" sx={{ mb: 2 }}>
                        {errorMessage}
                    </Typography>
                )}

                <Button
                    onClick={handleVerifyOTP}
                    fullWidth
                    variant="contained"
                    disabled={isVerifying || otp.join("").length !== 6}
                    sx={{
                        mt: 1,
                        mb: 3,
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: "1.05rem",
                    }}
                >
                    {isVerifying ? t("verifying") : t("verify")}
                </Button>

                <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        {t("didntReceiveCode")}
                        <Typography
                            component="span"
                            onClick={canResend && !isResending ? handleResendOTP : undefined}
                            color={canResend ? "primary" : "text.secondary"}
                            variant="body2"
                            fontWeight="bold"
                            ml={1}
                            sx={{
                                cursor: canResend && !isResending ? "pointer" : "default",
                            }}
                        >
                            {canResend
                                ? isResending
                                    ? t("resending")
                                    : t("resend")
                                : t("resendInSeconds", { seconds: resendTimer })}
                        </Typography>
                    </Typography>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export default OtpVerificationDialog;